import { HANDOVER_SHIFTS, HANDOVER_TEXT_MAX, NURSING_NOTE_MAX } from '@wodi/domain';
import { z } from 'zod';

import { actorSchema, isoDaySchema, RECORD_STATUSES } from './clinical-common.ts';
import { boardOccupantSchema } from './inpatient.ts';
import { vitalSignSetSchema } from './triage.ts';

// Ward rounds and nursing observations (docs/plans/phase2-ward-rounds-nursing.md §3). Note
// text is PHI: it travels only in bodies, never in URLs, logs or audit metadata.

// --- Ward rounds ------------------------------------------------------------------------------

export const startWardRoundBodySchema = z.object({
  /** Generated once per start; a retried request returns the same ward round. */
  clientRequestId: z.uuid(),
});

export const wardRoundStartedSchema = z.object({
  consultationId: z.uuid(),
  visitId: z.uuid(),
});

// --- Ward worklist ----------------------------------------------------------------------------

export const worklistRowSchema = z.object({
  occupant: boardOccupantSchema,
  bed: z.object({ id: z.uuid(), label: z.string() }).nullable(),
  /**
   * The clinical facts, or null when the reader may not see this patient (sensitivity
   * labels): a restricted record shows its bed and day of stay only.
   */
  clinical: z
    .object({
      /** Lab tests not yet reported, and procedures or imaging not yet done. */
      pendingResults: z.number().int().min(0),
      /** Orders and prescriptions placed in the last NEW_ORDER_WINDOW_HOURS. */
      newOrders: z.number().int().min(0),
      /** Critical lab results nobody has acknowledged yet. */
      unacknowledgedCriticals: z.number().int().min(0),
      lastVitalsAt: z.iso.datetime().nullable(),
      /** A ward-round note was signed on this East Africa day. */
      roundedToday: z.boolean(),
      /** A ward round is in progress now. */
      roundInProgress: z.boolean(),
      /** Medicine doses due now and not yet recorded (eMAR). */
      dosesDue: z.number().int().min(0),
      /** Doses past the facility's late window and not recorded; 0 when no window is set. */
      dosesOverdue: z.number().int().min(0),
    })
    .nullable(),
});

export type WorklistRow = z.infer<typeof worklistRowSchema>;

export const wardWorklistSchema = z.object({
  ward: z.object({ id: z.uuid(), name: z.string() }),
  rows: z.array(worklistRowSchema),
  newOrderWindowHours: z.number().int(),
});

export type WardWorklist = z.infer<typeof wardWorklistSchema>;

// --- Nursing notes ----------------------------------------------------------------------------

export const recordNursingNoteBodySchema = z.object({
  clientRequestId: z.uuid(),
  text: z.string().max(NURSING_NOTE_MAX * 2),
  /** When the note is about. Absent means now; not before admission, not in the future. */
  notedAt: z.iso.datetime().optional(),
});

export type RecordNursingNoteBody = z.infer<typeof recordNursingNoteBodySchema>;

export const nursingNoteSchema = z.object({
  id: z.uuid(),
  wardName: z.string(),
  notedAt: z.iso.datetime(),
  text: z.string(),
  status: z.enum(RECORD_STATUSES),
  statusReason: z.string().nullable(),
  recordedAt: z.iso.datetime(),
  recordedBy: actorSchema,
});

export type NursingNoteView = z.infer<typeof nursingNoteSchema>;

// --- The nursing view of a stay -----------------------------------------------------------------

export const earlyWarningViewSchema = z.discriminatedUnion('state', [
  z.object({ state: z.literal('not_configured') }),
  z.object({
    state: z.literal('scored'),
    scale: z.enum(['news2', 'pews']),
    score: z.number().int(),
    riskBand: z.string(),
  }),
]);

export const nursingViewSchema = z.object({
  admission: z.object({
    id: z.uuid(),
    patientId: z.uuid(),
    visitId: z.uuid(),
    status: z.enum(['admitted', 'discharged', 'cancelled']),
    admittedAt: z.iso.datetime(),
    daysAdmitted: z.number().int().min(1),
    ward: z.object({ id: z.uuid(), name: z.string() }).nullable(),
    bedLabel: z.string().nullable(),
  }),
  /** Age in days now, to judge what is typed against age-banded rules. */
  patientAgeDays: z.number().int().nullable(),
  /** Every vitals set of the stay, newest first, including corrections and entered in error. */
  vitalSets: z.array(vitalSignSetSchema),
  nursingNotes: z.array(nursingNoteSchema),
  /** TODO(clinical-verify): plan W5, always not configured until thresholds are approved. */
  earlyWarning: earlyWarningViewSchema,
});

export type NursingView = z.infer<typeof nursingViewSchema>;

// --- Ward handover ------------------------------------------------------------------------------

export const recordHandoverBodySchema = z.object({
  clientRequestId: z.uuid(),
  text: z.string().max(HANDOVER_TEXT_MAX * 2),
});

export type RecordHandoverBody = z.infer<typeof recordHandoverBodySchema>;

export const handoverNoteSchema = z.object({
  id: z.uuid(),
  shiftDate: isoDaySchema,
  shift: z.enum(HANDOVER_SHIFTS),
  text: z.string(),
  status: z.enum(RECORD_STATUSES),
  statusReason: z.string().nullable(),
  recordedAt: z.iso.datetime(),
  recordedBy: actorSchema,
});

export type HandoverNoteView = z.infer<typeof handoverNoteSchema>;

export const handoverNotesSchema = z.object({
  ward: z.object({ id: z.uuid(), name: z.string() }),
  /** The shift now, for "Write handover note". */
  current: z.object({ shiftDate: isoDaySchema, shift: z.enum(HANDOVER_SHIFTS) }),
  notes: z.array(handoverNoteSchema),
});

export type HandoverNotes = z.infer<typeof handoverNotesSchema>;
